import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { setInvoiceStatus, getWinTeamEnvironment, getBranding } from '../api';
import { useIsMobile } from '../hooks/useIsMobile';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071/api';

interface InvoiceSummary {
  tracking_id: string;
  vendor_name: string;
  invoice_number: string;
  invoice_date: string;
  invoice_amount: number;
  status: string;
  workflow_type: string;
  received_at: string;
  corrected_at: string | null;
  review_token: string | null;
  token_expires_at: string | null;
  token_used_at: string | null;
  token_valid: boolean;
}

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  CODED:     { bg: '#fff8e1', color: '#b8860b' },
  CORRECTED: { bg: '#e3f2fd', color: '#1565c0' },
  APPROVED:  { bg: '#e8f5e9', color: '#2d7a2d' },
  POSTED:    { bg: '#e8f5e9', color: '#1b5e20' },
  ERROR:     { bg: '#fdecea', color: '#c0392b' },
  DUPLICATE: { bg: '#f3e5f5', color: '#6a1b9a' },
  FLAGGED:   { bg: '#fff3e0', color: '#e65100' },
  REJECTED:  { bg: '#fdecea', color: '#b71c1c' },
  DELETED:   { bg: '#f5f5f5', color: '#9e9e9e' },
};

const STATUS_TABS: { label: string; statuses: string[] }[] = [
  { label: 'Pending',   statuses: ['CODED', 'CORRECTED'] },
  { label: 'Approved',  statuses: ['APPROVED'] },
  { label: 'Posted',    statuses: ['POSTED'] },
  { label: 'Error',     statuses: ['ERROR'] },
  { label: 'Duplicate', statuses: ['DUPLICATE'] },
  { label: 'All',       statuses: [] },
];

function ageLabel(dateStr: string): { label: string; color: string } {
  const hours = (Date.now() - new Date(dateStr).getTime()) / 36e5;
  if (hours < 24) return { label: `${Math.floor(hours)}h ago`,  color: '#2d7a2d' };
  if (hours < 48) return { label: `${Math.floor(hours)}h ago`,  color: '#b8860b' };
  return            { label: `${Math.floor(hours / 24)}d ago`, color: '#c0392b' };
}

export default function InvoiceList() {
  const { user, logout, isAdmin, isApprover, isPowerUser, isPOUser } = useAuth();
  const navigate = useNavigate();

  const [invoices, setInvoices]           = useState<InvoiceSummary[]>([]);
  const [filtered, setFiltered]           = useState<InvoiceSummary[]>([]);
  const [selected, setSelected]           = useState<Set<string>>(new Set());
  const [statusFilter, setStatusFilter]   = useState<string>('Pending');
  const [vendorFilter, setVendorFilter]   = useState('');
  const [searchQuery, setSearchQuery]     = useState('');   // debounced server-side search
  const [dateFilter, setDateFilter]       = useState<string>('all');
  const [loading, setLoading]             = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast]                 = useState<{ msg: string; ok: boolean } | null>(null);
  const [counts, setCounts]               = useState<Record<string, number>>({});
  const [statusModal, setStatusModal]     = useState(false);
  const [newStatus, setNewStatus]         = useState('CODED');
  const [uploadResults, setUploadResults] = useState<{
    successes: number;
    failures: { vendor_name: string; invoice_number: string; error: string; error_code: string }[];
  } | null>(null);
  const [statusReason, setStatusReason]   = useState('');
  const [wtEnvironment, setWtEnvironment] = useState<string | null>(null);
  const [wtDbName, setWtDbName]           = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [reverseLogoUrl, setReverseLogoUrl] = useState('');
  const [, setPortalTab]                    = useState('');
  const isMobile = useIsMobile();

  const showToast = (msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4000);
  };

  // Debounce the search input — wait 400 ms after the user stops typing
  // before committing to searchQuery, which triggers a server-side re-fetch.
  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(vendorFilter.trim()), 400);
    return () => clearTimeout(timer);
  }, [vendorFilter]);

  const loadInvoices = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      // Build query params: date range and/or search term are pushed to the
      // server so the LIMIT 5000 applies after filtering, not before.
      const qp: string[] = [];
      if (dateFilter !== 'all') {
        const days = dateFilter === 'today' ? 1 : dateFilter === '7days' ? 7 : 30;
        const cutoff = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
        qp.push(`date_from=${encodeURIComponent(cutoff.toISOString())}`);
      }
      if (searchQuery) {
        qp.push(`search=${encodeURIComponent(searchQuery)}`);
      }
      const url = qp.length ? `${API_BASE}/invoices?${qp.join('&')}` : `${API_BASE}/invoices`;
      const { data } = await axios.get<InvoiceSummary[]>(url);
      setInvoices(data);
      const c: Record<string, number> = {};
      data.forEach(inv => { c[inv.status] = (c[inv.status] || 0) + 1; });
      setCounts(c);
      setLastRefreshed(new Date());
    } catch {
      if (!silent) showToast('Failed to load invoices', false);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [dateFilter, searchQuery]);

  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  // Auto-refresh every 30 seconds (silent — no spinner)
  useEffect(() => {
    const interval = setInterval(() => loadInvoices(true), 30_000);
    return () => clearInterval(interval);
  }, [loadInvoices]);

  useEffect(() => {
    getWinTeamEnvironment()
      .then(r => { setWtEnvironment(r.environment); setWtDbName(r.db_name); })
      .catch(() => { setWtEnvironment(null); setWtDbName(null); });
  }, []);

  useEffect(() => {
    getBranding().then(b => {
      if (b.reverse_logo_url) setReverseLogoUrl(b.reverse_logo_url);
      if (b.portal_tab) { setPortalTab(b.portal_tab); document.title = b.portal_tab; }
    });
  }, []);

  // Apply client-side status tab filter only.
  // Date and vendor/invoice search are handled server-side.
  useEffect(() => {
    const tab = STATUS_TABS.find(t => t.label === statusFilter);
    let result = invoices;
    if (tab && tab.statuses.length > 0) {
      result = result.filter(i => tab.statuses.includes(i.status));
    }
    setFiltered(result);
    setSelected(new Set());
  }, [invoices, statusFilter]);

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(i => i.tracking_id)));
    }
  };

  const renewTokens = async () => {
    if (!selected.size) return;
    setActionLoading(true);
    try {
      const ids = Array.from(selected);
      await axios.post(`${API_BASE}/invoices/renew-tokens`, { tracking_ids: ids });
      showToast(`Tokens renewed and emails re-sent for ${ids.length} invoice(s)`);
      await loadInvoices();
      setSelected(new Set());
    } catch {
      showToast('Failed to renew tokens', false);
    } finally {
      setActionLoading(false);
    }
  };

  const uploadToWinTeam = async () => {
    if (!selected.size) return;
    const approvedSelected = filtered.filter(
      i => selected.has(i.tracking_id) && ['CODED', 'CORRECTED', 'APPROVED'].includes(i.status)
    );
    if (!approvedSelected.length) {
      showToast('No uploadable invoices selected — only CODED, CORRECTED, or APPROVED invoices can be uploaded', false);
      return;
    }
    if (!window.confirm(
      `Upload ${approvedSelected.length} invoice(s) to WinTeam?\n\n` +
      approvedSelected.map(i => `• ${i.vendor_name} — ${i.invoice_number} — $${i.invoice_amount.toFixed(2)} (${i.status})`).join('\n')
    )) return;

    setActionLoading(true);
    let successes = 0;
    const failures: { vendor_name: string; invoice_number: string; error: string; error_code: string }[] = [];

    for (const inv of approvedSelected) {
      try {
        await axios.post(`${API_BASE}/post-to-winteam`, { tracking_id: inv.tracking_id });
        successes++;
      } catch (err: any) {
        const data = err?.response?.data;
        failures.push({
          vendor_name:    inv.vendor_name,
          invoice_number: inv.invoice_number,
          error:          data?.error || 'Unknown error',
          error_code:     data?.error_code || '',
        });
      }
    }

    await loadInvoices();
    setSelected(new Set());
    setActionLoading(false);

    if (failures.length === 0) {
      showToast(`${successes} invoice(s) uploaded to WinTeam successfully`);
    } else {
      setUploadResults({ successes, failures });
    }
  };

  const confirmChangeStatus = async () => {
    if (!selected.size || !newStatus) return;
    setActionLoading(true);
    setStatusModal(false);
    try {
      const ids = Array.from(selected);
      const result = await setInvoiceStatus(ids, newStatus, statusReason);
      showToast(`${result.updated_count} invoice(s) updated to ${newStatus}`);
      await loadInvoices();
      setSelected(new Set());
      setStatusReason('');
    } catch {
      showToast('Failed to update invoice status', false);
    } finally {
      setActionLoading(false);
    }
  };

  const totalPending = invoices
    .filter(i => ['CODED', 'CORRECTED'].includes(i.status))
    .reduce((sum, i) => sum + i.invoice_amount, 0);

  const expiringSoon = invoices.filter(i =>
    i.token_valid && i.token_expires_at &&
    (new Date(i.token_expires_at).getTime() - Date.now()) < 8 * 36e5
  ).length;

  const errorCount = counts['ERROR'] || 0;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f0f2f5', fontFamily: 'Segoe UI, sans-serif' }}>

      {/* ── Header ── */}
      <div style={{ backgroundColor: '#00386C', padding: isMobile ? '0 12px' : '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '56px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {reverseLogoUrl
            ? <img src={reverseLogoUrl} alt="Logo" style={{ height: '28px', objectFit: 'contain', display: 'block' }} />
            : <span style={{ color: 'white', fontWeight: 700, fontSize: '16px' }}>AP Portal</span>
          }
          {isAdmin && (
            <span style={{ backgroundColor: '#b8860b', color: 'white', fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '10px' }}>
              ADMIN
            </span>
          )}
          {wtEnvironment && (
            <span style={{
              backgroundColor: wtEnvironment === 'PRODUCTION' ? '#c0392b' : '#1a7a4a',
              color: 'white',
              fontSize: '11px',
              fontWeight: 700,
              padding: '2px 8px',
              borderRadius: '10px',
              letterSpacing: '0.5px',
            }}>
              {wtEnvironment === 'PRODUCTION' ? '⚠ PRODUCTION' : 'TRAINING'}{wtDbName ? ` — ${wtDbName}` : ''}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '16px' }}>
          {isPOUser && (
            <button onClick={() => navigate('/purchase-orders')} style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.4)', color: 'white', padding: isMobile ? '4px 8px' : '4px 14px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
              {isMobile ? 'PO' : 'Purchase Orders'}
            </button>
          )}
          {isPowerUser && (
            <button onClick={() => navigate('/credit-card-upload')} style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.4)', color: 'white', padding: isMobile ? '4px 8px' : '4px 14px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
              {isMobile ? 'CC' : 'Credit Card Upload'}
            </button>
          )}
          {isAdmin && (
            <button onClick={() => navigate('/admin')} style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.4)', color: 'white', padding: isMobile ? '4px 8px' : '4px 14px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
              {isMobile ? 'Admin' : 'Admin Panel'}
            </button>
          )}
          <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px', display: isMobile ? 'none' : 'inline' }}>{user?.display_name}</span>
          <button onClick={logout} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.4)', color: 'white', padding: '4px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>
            Sign out
          </button>
        </div>
      </div>

      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: isMobile ? '12px' : '24px' }}>

        {/* ── Summary tiles ── */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
          {[
            { label: 'Awaiting Upload',     value: (counts['CODED'] || 0) + (counts['CORRECTED'] || 0), color: '#00386C' },
            { label: 'Total Pending Value', value: `$${totalPending.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, color: '#00386C' },
            { label: 'Tokens Expiring Soon', value: expiringSoon, color: expiringSoon > 0 ? '#b8860b' : '#2d7a2d' },
            { label: 'Upload Errors',        value: errorCount,   color: errorCount > 0  ? '#c0392b' : '#2d7a2d' },
          ].map(tile => (
            <div key={tile.label} style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
              <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{tile.label}</p>
              <p style={{ margin: 0, fontSize: '28px', fontWeight: 700, color: tile.color }}>{tile.value}</p>
            </div>
          ))}
        </div>

        {/* ── Filter bar ── */}
        <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>

          {/* Status tabs */}
          {STATUS_TABS.map(tab => {
            const count = tab.statuses.reduce((sum, s) => sum + (counts[s] || 0), 0);
            return (
              <button
                key={tab.label}
                onClick={() => setStatusFilter(tab.label)}
                style={{
                  padding: '6px 14px', borderRadius: '20px', border: 'none',
                  cursor: 'pointer', fontSize: '13px', fontWeight: 600,
                  backgroundColor: statusFilter === tab.label ? '#00386C' : '#f0f2f5',
                  color: statusFilter === tab.label ? 'white' : '#555',
                }}
              >
                {tab.label}{tab.statuses.length > 0 && count > 0 ? ` (${count})` : ''}
              </button>
            );
          })}

          {/* Search */}
          <input
            placeholder="Search vendor or invoice #"
            value={vendorFilter}
            onChange={e => setVendorFilter(e.target.value)}
            style={{ marginLeft: isMobile ? '0' : 'auto', padding: '6px 12px', border: '1px solid #ddd', borderRadius: '20px', fontSize: '13px', width: isMobile ? '100%' : '220px', outline: 'none', flexBasis: isMobile ? '100%' : 'auto' }}
          />

          {/* Date filter */}
          <div style={{ display: 'flex', gap: '4px' }}>
            {[
              { label: 'Today',   value: 'today' },
              { label: '7 Days',  value: '7days' },
              { label: '30 Days', value: '30days' },
              { label: 'All',     value: 'all' },
            ].map(d => (
              <button
                key={d.value}
                onClick={() => setDateFilter(d.value)}
                style={{
                  padding: '5px 12px', borderRadius: '20px', border: 'none',
                  cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                  backgroundColor: dateFilter === d.value ? '#00386C' : '#f0f2f5',
                  color: dateFilter === d.value ? 'white' : '#555',
                }}
              >
                {d.label}
              </button>
            ))}
          </div>

          {/* Live refresh indicator */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '8px' }}>
            <span style={{
              display: 'inline-block', width: '7px', height: '7px', borderRadius: '50%',
              backgroundColor: '#2d7a2d',
              boxShadow: '0 0 0 2px rgba(45,122,45,0.25)',
            }} />
            <span style={{ fontSize: '11px', color: '#999', whiteSpace: 'nowrap' }}>
              {lastRefreshed
                ? `Updated ${lastRefreshed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`
                : 'Live'}
            </span>
          </div>
        </div>

        {/* ── Batch action bar ── */}
        {selected.size > 0 && (
          <div style={{ backgroundColor: '#e8f0fe', border: '1px solid #c5d4f7', borderRadius: '8px', padding: '12px 20px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#1565c0' }}>{selected.size} invoice(s) selected</span>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
              {isApprover && (
                <>
                  <button
                    onClick={renewTokens}
                    disabled={actionLoading}
                    style={{ padding: '7px 16px', backgroundColor: 'white', border: '1px solid #1565c0', color: '#1565c0', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
                  >
                    🔄 Renew Tokens
                  </button>
                  {filtered.some(i => selected.has(i.tracking_id) && ['CODED', 'CORRECTED', 'APPROVED'].includes(i.status)) && (
                    <button
                      onClick={uploadToWinTeam}
                      disabled={actionLoading}
                      style={{ padding: '7px 16px', backgroundColor: '#00386C', border: 'none', color: 'white', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
                    >
                      ⬆️ Upload to WinTeam
                    </button>
                  )}
                </>
              )}
              {isAdmin && (
                <button
                  onClick={() => { setNewStatus('CODED'); setStatusReason(''); setStatusModal(true); }}
                  disabled={actionLoading}
                  style={{ padding: '7px 16px', backgroundColor: '#b8860b', border: 'none', color: 'white', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
                >
                  ✏️ Change Status
                </button>
              )}
              <button
                onClick={() => setSelected(new Set())}
                style={{ padding: '7px 16px', backgroundColor: 'white', border: '1px solid #ddd', color: '#555', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}
              >
                Clear
              </button>
            </div>
          </div>
        )}

        {/* ── Invoice table ── */}
        <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '48px', textAlign: 'center', color: '#999' }}>Loading invoices...</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '48px', textAlign: 'center', color: '#999' }}>No invoices match the current filter.</div>
          ) : (
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' as any }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '700px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e9ecef' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', width: '40px' }}>
                    <input type="checkbox" checked={selected.size === filtered.length && filtered.length > 0} onChange={toggleSelectAll} />
                  </th>
                  {['Vendor', 'Invoice #', 'Date', 'Amount', 'Status', 'Token', 'Age', ''].map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#555', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((inv, i) => {
                  const age      = ageLabel(inv.received_at);
                  const sc       = STATUS_COLORS[inv.status] || { bg: '#f0f2f5', color: '#555' };
                  const isSelected = selected.has(inv.tracking_id);
                  return (
                    <tr
                      key={inv.tracking_id}
                      style={{ backgroundColor: isSelected ? '#f0f4ff' : i % 2 === 0 ? 'white' : '#fafafa', borderBottom: '1px solid #e9ecef', cursor: 'pointer' }}
                      onClick={() => navigate(`/review?tracking_id=${inv.tracking_id}`)}
                    >
                      <td style={{ padding: '12px 16px' }} onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(inv.tracking_id)} />
                      </td>
                      <td style={{ padding: '12px 16px', fontWeight: 600, color: '#1a1a1a' }}>{inv.vendor_name}</td>
                      <td style={{ padding: '12px 16px', color: '#444' }}>{inv.invoice_number}</td>
                      <td style={{ padding: '12px 16px', color: '#666' }}>{inv.invoice_date?.slice(0, 10)}</td>
                      <td style={{ padding: '12px 16px', fontWeight: 600 }}>${inv.invoice_amount.toFixed(2)}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 700, backgroundColor: sc.bg, color: sc.color }}>{inv.status}</span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        {inv.workflow_type === 'CC_RECON' ? (
                          <span style={{ color: '#999', fontSize: '12px' }}>—</span>
                        ) : inv.token_used_at ? (
                          <span style={{ color: '#999', fontSize: '12px' }}>Used</span>
                        ) : inv.token_valid ? (
                          <span style={{ color: '#2d7a2d', fontSize: '12px' }}>✓ Valid</span>
                        ) : (
                          <span style={{ color: '#c0392b', fontSize: '12px' }}>Expired</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px', color: age.color, fontWeight: 600, fontSize: '12px' }}>{age.label}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ color: '#00386C', fontSize: '12px', fontWeight: 600 }}>Review →</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </div>

      </div>

      {/* ── Upload Results Modal ── */}
      {uploadResults && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '8px', padding: '32px',
            maxWidth: '560px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            fontFamily: 'inherit', borderTop: `4px solid ${uploadResults.failures.length > 0 ? '#c0392b' : '#2d7a2d'}`,
            maxHeight: '80vh', overflowY: 'auto',
          }}>
            <h2 style={{ margin: '0 0 6px 0', fontSize: '18px', color: '#00386C' }}>Upload Results</h2>
            <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: '#666' }}>
              {uploadResults.successes > 0 && (
                <span style={{ color: '#2d7a2d', fontWeight: 600 }}>✓ {uploadResults.successes} uploaded successfully. </span>
              )}
              {uploadResults.failures.length > 0 && (
                <span style={{ color: '#c0392b', fontWeight: 600 }}>{uploadResults.failures.length} failed — see details below.</span>
              )}
            </p>

            {uploadResults.failures.map((f, i) => (
              <div key={i} style={{
                backgroundColor: '#fdecea', border: '1px solid #f5c6cb', borderRadius: '6px',
                padding: '12px 14px', marginBottom: '10px',
              }}>
                <div style={{ fontWeight: 600, fontSize: '13px', color: '#1a202c', marginBottom: '4px' }}>
                  {f.vendor_name} — {f.invoice_number}
                </div>
                <div style={{ fontSize: '12px', color: '#c0392b' }}>
                  <strong>{f.error_code || 'ERROR'}:</strong> {f.error}
                </div>
              </div>
            ))}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                onClick={() => setUploadResults(null)}
                style={{ padding: '9px 24px', backgroundColor: '#00386C', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Status Change Modal (Admin only) ── */}
      {statusModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '8px', padding: '32px',
            maxWidth: '480px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            fontFamily: 'inherit', borderTop: '4px solid #b8860b',
          }}>
            <h2 style={{ margin: '0 0 6px 0', fontSize: '18px', color: '#00386C' }}>Change Invoice Status</h2>
            <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: '#666' }}>
              Updating <strong>{selected.size}</strong> invoice(s). This action is logged for audit purposes.
            </p>

            <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#4a5568', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
              New Status
            </label>
            <select
              value={newStatus}
              onChange={e => setNewStatus(e.target.value)}
              style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e0', borderRadius: '4px', fontSize: '14px', marginBottom: '16px', fontFamily: 'inherit', color: '#1a202c' }}
            >
              {['CODED', 'CORRECTED', 'APPROVED', 'POSTED', 'FLAGGED', 'ERROR', 'DUPLICATE', 'REJECTED', 'DELETED'].map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>

            <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#4a5568', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
              Reason (optional)
            </label>
            <textarea
              value={statusReason}
              onChange={e => setStatusReason(e.target.value)}
              placeholder="Describe why this status change is needed…"
              style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e0', borderRadius: '4px', fontSize: '14px', fontFamily: 'inherit', minHeight: '72px', boxSizing: 'border-box', resize: 'vertical', marginBottom: '24px' }}
            />

            {newStatus === 'DELETED' && (
              <div style={{ backgroundColor: '#fff0ee', border: '1px solid #fed7d7', borderRadius: '4px', padding: '10px 14px', marginBottom: '20px', fontSize: '13px', color: '#c0392b' }}>
                ⚠️ <strong>DELETED</strong> status will hide this invoice from all views. This cannot be undone from the portal.
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setStatusModal(false)}
                style={{ padding: '9px 20px', border: '1px solid #cbd5e0', borderRadius: '4px', backgroundColor: 'white', cursor: 'pointer', fontSize: '14px', color: '#4a5568', fontFamily: 'inherit' }}
              >
                Cancel
              </button>
              <button
                onClick={confirmChangeStatus}
                style={{ padding: '9px 20px', border: 'none', borderRadius: '4px', backgroundColor: newStatus === 'DELETED' ? '#c0392b' : '#b8860b', cursor: 'pointer', fontSize: '14px', color: 'white', fontFamily: 'inherit', fontWeight: 700 }}
              >
                Confirm — Set to {newStatus}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Toast notification ── */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '24px', right: '24px',
          backgroundColor: toast.ok ? '#2d7a2d' : '#c0392b',
          color: 'white', padding: '12px 20px', borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)', fontSize: '14px', fontWeight: 500,
          maxWidth: '400px', zIndex: 1000
        }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
